import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';

import { PrettyLoadPreloader } from 'components/PrettyLoadPreloader';
import { useStore } from 'hooks/useStore';
import logger from 'sentry.client.config';
import { SiteFeaturesIDs } from 'store/SiteFeatures/SiteFeatures';
import { LoadType } from 'types/cargosApp/Load';
import { IReactLoadsGridProps } from 'types/global';

type TPrettyLoadProps = Omit<IReactLoadsGridProps, 'siteFeatures' | 'culture'>;

function addMockSimilarLoads(loads: LoadType[]): LoadType[] {
  return loads.map((load, index) => {
    const hasSimilar = Math.random() > 0.5;
    if (!hasSimilar) return load;

    const count = Math.floor(Math.random() * 5) + 1;
    const neighbors = loads.filter((_, i) => i !== index);
    const similar = neighbors.slice(0, count).map(neighbor => ({ ...neighbor }));

    return { ...load, similar };
  });
}

const Fallback = () => <div />;
const LoadsGrid = dynamic(
  () =>
    // @ts-ignore
    import('PrettyLoad/PrettyLoad').catch(error => {
      logger.error(error);
      // eslint-disable-next-line no-console
      console.log('error with PrettyLoad loading: ', error);
      return Fallback;
    }),
  {
    loading: () => <PrettyLoadPreloader />,
    ssr: false,
  },
);

const PrettyLoad = (props: TPrettyLoadProps) => {
  const { callbacks, descriptor, currentPage, loads, updateLoad, ...searchResult } = props;

  const {
    app: { locale, loading, serverDate },
    siteFeatures: { isSiteFeatureEnabled },
    enablerFeaturesRepo,
    ui: { isCompactPrettyLoad, isPrintMode, setPrintMode, vatRatesDisplay },
    dictionaries: { data },
    boardsRepo: { boardsData },
  } = useStore();

  const isSimilarCargoEnabled = isSiteFeatureEnabled[SiteFeaturesIDs.similarCargoApplications];
  const isVatRatesFeatureEnabled = isSiteFeatureEnabled[SiteFeaturesIDs.newVatRates];

  const processedLoads = useMemo(() => {
    if (!loads || !isSimilarCargoEnabled) return loads;
    return addMockSimilarLoads(loads);
  }, [loads, isSimilarCargoEnabled]);

  if (loading.search) {
    return <PrettyLoadPreloader />;
  }

  const shouldSkipRenderPrettyLoad = typeof window === 'undefined' || !loads || !loads.length;
  if (shouldSkipRenderPrettyLoad) return null;

  return (
    <LoadsGrid
      {...searchResult}
      // @ts-ignore
      boards={boardsData}
      callbacks={callbacks}
      culture={locale}
      currentPage={currentPage}
      descriptor={descriptor}
      dictionaries={data}
      enabler={enablerFeaturesRepo}
      isCompactAll={isCompactPrettyLoad}
      isPrintMode={isPrintMode}
      loads={processedLoads}
      selectedVatPercentsForDisplay={
        isVatRatesFeatureEnabled ? vatRatesDisplay.selectedVatPercentsForDisplay : undefined
      }
      serverDate={serverDate}
      setPrintMode={setPrintMode}
      siteFeatures={isSiteFeatureEnabled}
      updateLoad={updateLoad}
      userId={window.atiUser.profile?.ati_code}
    />
  );
};

const connected = observer(PrettyLoad);

export { connected as PrettyLoad };
